import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { PagedResponse, Response } from 'src/app/models/response.model';
import { ApiService } from 'src/app/services/api.service';
import { stagger40ms } from 'src/@vex/animations/stagger.animation';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { fadeInUp400ms } from 'src/@vex/animations/fade-in-up.animation';
import { EventBusService } from 'src/app/services/event-bus.service';
import { MatSelect } from '@angular/material/select';
import { LookupService } from 'src/app/services/lookup.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ReplaySubject, Subject, takeUntil } from 'rxjs';
import { HttpEvent, HttpResponse } from '@angular/common/http';

export interface EnrollmentDebitByServiceProviderDto {
  enrollmentId: number;
  enrollmentName: string;
  visaStatus: number;
  currencyId: number;
  totalDebit: number;
  totalPayment: number;
  totalDiff: number;
  transactions: ServiceProviderOperationCreditTransactionDto[];

  expanded: boolean;
}

export interface ServiceProviderOperationCreditTransactionDto {
  date: string;
  type: number;
  relatedBranchName: string;
  relatedBranchId: number | null;
  executorUserName: string;
  executorUserId: number;
  accountName: string;
  accountId: number | null;
  currencyId: number;
  amount: number;
  note: string;
}

@Component({
  selector: 'utclub-debit-service-provider-report',
  templateUrl: './service-provider-debits.component.html',
  styleUrls: ['./service-provider-debits.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ]),
    fadeInUp400ms,
    stagger40ms
  ]
})
export class ServiceProviderDebitsComponent implements OnInit, OnDestroy {
  formGroup: FormGroup = new FormGroup({
    serviceProviderId: new FormControl(),
    year: new FormControl(new Date().getFullYear())
  });
  searchCtrl = new FormControl();
  @ViewChild('input') input: MatSelect;

  totalDebit: number;
  totalPayment: number;
  totalDiff: number;

  data: EnrollmentDebitByServiceProviderDto[];
  loading = false;
  constructor(
    private apiService: ApiService,
    private eventBusService: EventBusService,
    private cdr: ChangeDetectorRef,
    private lookup: LookupService,
    private snackbar: MatSnackBar
  ) {}

  items: any[];
  loadingItems: boolean = false;
  anyErrors: boolean = false;
  filteredItems: ReplaySubject<any[]> = new ReplaySubject<any[]>();
  _onDestroy = new Subject<void>();
  onSubmit() {
    this.totalPayment = 0;
    this.totalDebit = 0;
    this.totalDiff = 0;
    this.loading = true;
    this.cdr.detectChanges();
    var url = `accounting-service/debit/enrollment-debits-by-service-provider/${this.formGroup.value.serviceProviderId}?year=${this.formGroup.value.year}`;
    this.apiService.get<Response<EnrollmentDebitByServiceProviderDto[]>>(url).subscribe({
      next: (response) => {
        response.data.forEach((item: EnrollmentDebitByServiceProviderDto) => {
          this.totalDebit += item.totalDebit;
          this.totalPayment += item.totalPayment;
        });
        this.totalDiff = this.totalDebit - this.totalPayment;

        this.data = response.data;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.snackbar.open(error.error.message, 'Close', { duration: 5000 });
      }
    });
  }

  ngOnInit(): void {
    this.loadItems();
    this.searchCtrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => {
      this.filterItems();
    });
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  filterItems() {
    if (!this.items) {
      return;
    }
    // get the search keyword
    let search = this.searchCtrl.value;
    if (!search) {
      this.filteredItems.next(this.items.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the banks
    this.filteredItems.next(this.items.filter((x) => x.label.toLowerCase().indexOf(search) > -1));
  }
  loadItems() {
    this.loadingItems = true;
    this.anyErrors = false;
    var url = 'accounting-service/serviceprovider';
    this.apiService.request('get', url).subscribe({
      next: (response: HttpEvent<Response<any>>) => {
        if (response instanceof HttpResponse) {
          var items = response.body.data.map((item: any) => {
            return {
              value: item.id,
              label: item.name
            };
          });
          this.items = items;
          this.filteredItems.next(this.items.slice());
          this.loadingItems = false;
          this.cdr.detectChanges();
        }
      },
      error: (error) => {
        this.anyErrors = true;
        this.loadingItems = false;
        this.cdr.detectChanges();
      },
      complete: () => {
        this.loadingItems = false;
        this.cdr.detectChanges();
      }
    });
  }
}
